import React, { useMemo, useState } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import { toMiamiDateString } from './utils/dateUtils';
import { buildSeedsReport, sumBags, visibleBags, seedMatches } from './utils/seedsReceivingReport';
import './reports.css';

const PERIODS = [{ key: 'day', label: 'Day' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }];
const COL_ID = 90;
const COL_SEED = 300;
const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
const lbs = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const scanLabel = (b) => { const [, m, d] = b.date.split('-').map(Number); return `${MONTHS[m - 1]} ${d}, ${b.time}`; };

const SOURCE_NOTE = {
  scan:       'Weight recorded at scan time',
  history:    'Weight taken from the first change in the bag history (bag scanned before scan weights were logged)',
  production: 'Weight taken from the production record that used this bag (no bag history)',
  current:    'Bag never used since scanning — current weight',
};

export default function SeedsReceivingReportPage() {
  const today = toMiamiDateString(new Date());
  const [from,     setFrom]     = useState(today.slice(0, 8) + '01');
  const [to,       setTo]       = useState(today);
  const [period,   setPeriod]   = useState('day');
  const [report,   setReport]   = useState(null);   // { bags, from, to }
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const datesOk = isDate(from) && isDate(to) && from <= to;

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/api/reports/seeds-receiving', { params: { from, to } });
      setReport({ bags: r.data.bags, from, to });
      setExpanded(new Set());
      setSearch('');
    } catch (err) {
      setError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const built = useMemo(() => (report ? buildSeedsReport(report.bags, period) : null), [report, period]);
  const totals = useMemo(() => (report ? sumBags(report.bags.map((b) => ({ ...b, periodKey: 'all' }))) : null), [report]);
  const unknown = report ? report.bags.filter((b) => b.weight == null).length : 0;

  const q = search.trim().toLowerCase();
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="gor-canvas">
      <HeaderLogged />
      <div className="gor-page">

        <div className="gor-header">
          <h2>Seeds Receiving Report</h2>
          <p className="gor-subtitle">Seed bags scanned into the warehouse (Scanners → Inventory Seeds), by scan date and time · weights in lbs</p>
        </div>

        {error && <div className="gor-error">{error}</div>}

        {/* Filters */}
        <div className="gor-card gor-filters">
          <div className="gor-row">
            <div className="gor-field">
              <label className="gor-label">Date from *</label>
              <input type="date" className="gor-input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="gor-field">
              <label className="gor-label">Date to *</label>
              <input type="date" className="gor-input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="gor-field">
              <label className="gor-label">Columns by</label>
              <div className="gor-seg">
                {PERIODS.map((p) => (
                  <button key={p.key} type="button" className={`gor-seg-btn${period === p.key ? ' is-active' : ''}`} onClick={() => setPeriod(p.key)}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="gor-field gor-field-btn">
              <button className="gor-btn" onClick={generate} disabled={!datesOk || loading}>
                {loading ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>

        {!report && !loading && (
          <div className="gor-empty">Set the scan period, then click <strong>Generate</strong>.</div>
        )}

        {/* Results */}
        {built && (
          <div className="gor-card gor-results">
            <div className="gor-toolbar">
              <input type="search" className="gor-input gor-search" placeholder="Search by seed name, Seed ID, LOT or BAG ID…"
                value={search} onChange={(e) => setSearch(e.target.value)} />
              <div className="gor-summary">
                <span><strong>{report.bags.length}</strong> bag{report.bags.length === 1 ? '' : 's'}</span>
                <span><strong>{lbs(totals.total)}</strong> lbs</span>
                <span>{report.from} – {report.to}</span>
                {unknown > 0 && (
                  <span className="gor-undated" title="Bags scanned before scan weights were logged and already used up in production without a history record: their received weight is unknown and excluded from the sums">
                    {unknown} bag{unknown === 1 ? '' : 's'} without a recorded weight
                  </span>
                )}
              </div>
            </div>

            {built.periods.length === 0 ? (
              <div className="gor-empty">No bags scanned in this period.</div>
            ) : (
              <div className="gor-table-wrap">
                <table className="gor-table">
                  <thead>
                    <tr>
                      <th className="gor-th gor-sticky" style={{ left: 0, minWidth: COL_ID, maxWidth: COL_ID }}>Seed ID</th>
                      <th className="gor-th gor-sticky" style={{ left: COL_ID, minWidth: COL_SEED, maxWidth: COL_SEED }}>Seed</th>
                      {built.periods.map((p) => <th key={p.key} className="gor-th gor-th-period gor-num">{p.label}</th>)}
                      <th className="gor-th gor-th-period gor-num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {built.seeds.map((s) => {
                      const bags = visibleBags(s, q);
                      if (!bags) return null;
                      const viaBags = !!q && !seedMatches(s, q);   // matched by LOT / BAG ID: open the row
                      const isOpen = expanded.has(s.seedsID) || viaBags;
                      const { cells, total } = sumBags(bags);
                      return (
                        <React.Fragment key={s.seedsID}>
                          <tr className="gor-group" onClick={() => toggle(s.seedsID)}>
                            <td className="gor-td gor-sticky gor-group-name" style={{ left: 0 }}>
                              <span className="gor-chevron">{isOpen ? '▾' : '▸'}</span>{s.seedsID}
                            </td>
                            <td className="gor-td gor-sticky gor-group-name" style={{ left: COL_ID }}>
                              {s.name}<span className="gor-count">{bags.length} bag{bags.length === 1 ? '' : 's'}</span>
                            </td>
                            {built.periods.map((p) => (
                              <td key={p.key} className={`gor-td gor-num${cells[p.key] == null ? ' gor-muted' : ''}`}>{lbs(cells[p.key])}</td>
                            ))}
                            <td className="gor-td gor-num">{lbs(total)}</td>
                          </tr>
                          {isOpen && bags.map((b) => (
                            <tr key={`${s.seedsID}|${b.bagId}`} className="gor-item">
                              <td className="gor-td gor-sticky" style={{ left: 0 }} title={SOURCE_NOTE[b.weightSource] || ''}>{b.bagId}</td>
                              <td className="gor-td gor-sticky gor-muted" style={{ left: COL_ID }}>
                                LOT {b.lot || '—'} · {scanLabel(b)}
                              </td>
                              {built.periods.map((p) => (
                                <td key={p.key} className={`gor-td gor-num${p.key === b.periodKey ? '' : ' gor-muted'}`}>
                                  {p.key === b.periodKey ? (b.weight == null ? 'n/a' : lbs(b.weight)) : ''}
                                </td>
                              ))}
                              <td className={`gor-td gor-num${b.weight == null ? ' gor-muted' : ''}`}>{b.weight == null ? 'n/a' : lbs(b.weight)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
